import JSZip from 'jszip';
import {
  BufferGeometry,
  Float32BufferAttribute,
  Matrix4,
  Mesh,
  Points,
  Vector3,
} from 'three';
import {
  BufferReader,
  DELTAFACE_FILE_NAMES,
  DELTAFACE_FILES,
  GINGIVA,
  type Gingiva,
} from '@witmetrics/unison-core';
import { filterOutKeyValue } from '@witmetrics/utils/arrays';
import { isZip, parseZipFile } from '@witmetrics/utils/files';
import {
  buildBufferGeometry,
  buildGingivaMesh,
  buildStepNumberMesh,
  buildToothMesh,
  buildWireMesh,
} from './meshes';
import { buildHdlMaterial, buildPointsMaterial } from './materials';
import type {
  DeltafaceBuffers,
  DeltafaceConfig,
  MeshMap,
  ParsedIndices,
  ParsedVertices,
  Vector3Map,
} from './types';

export function createDeltafaceConfig(): DeltafaceConfig {
  return {
    attachments: new Map(),
    frames: new Map([]),
    gingivas: new Map([]),
    hasIpr: undefined,
    ipr: new Map(), // Inter-proximal reduction
    iprPointsScene: new Map(),
    iprValues: new Map(),
    isAttachmentsVisible: true,
    isIprVisible: false, // TODO
    isMandibleVisible: true,
    isMaxillaVisible: true,
    mandibleGingivas: new Map([]),
    maxillaGingivas: new Map([]),
    missingTeeth: [],
    numbers: new Map([]), //3D meshes for numbers
    optionalMeshes: new Map([]), //Meshes for optional meshes in scene
    optionalMeshesFrames: new Map(),
    optionalMeshesToothNumbers: new Map(),
    optionalMeshesTypes: new Map([]),
    optionalMeshesVisible: new Map(), //For each subStep know if attachment is visible
    posStartGingivas: new Map([]),
    teeth: new Map([]),
    totalSteps: 0,
    visibleIprs: new Map(),
    wiresMandible: new Map(),
    wiresMaxilla: new Map(),
  };
}

function parseVertices(reader: BufferReader, count: number): ParsedVertices {
  let vertices = [];
  let dataOffset = reader.offset;
  for (let i = 0; i < count; i++) {
    vertices.push(
      reader.dataView.getFloat64(dataOffset), // x
      reader.dataView.getFloat64(dataOffset + 8), // y (1 * 8 bytes)
      reader.dataView.getFloat64(dataOffset + 16) // z (2 * 8 bytes)
    );
    dataOffset += 24; // 3 * 8 bytes
  }
  return { vertices, dataOffset };
}

function parseIndices(reader: BufferReader, count: number): ParsedIndices {
  let indices = [];
  let dataOffset = reader.offset;
  for (let i = 0; i < count; i++) {
    indices.push(
      reader.dataView.getInt32(dataOffset), // index0
      reader.dataView.getInt32(dataOffset + 4), // index1 (1 * 4 bytes)
      reader.dataView.getInt32(dataOffset + 8) // index2 (2 * 4 bytes)
    );
    dataOffset += 12; // (3 * 4 bytes)
  }
  return { indices, dataOffset };
}

function buildMatrix(values: number[]): Matrix4 {
  const matrix = new Matrix4();
  matrix.set(
    values[0],
    values[1],
    values[2],
    values[3],
    values[4],
    values[5],
    values[6],
    values[7],
    values[8],
    values[9],
    values[10],
    values[11],
    values[12],
    values[13],
    values[14],
    values[15]
  );
  return matrix;
}

export function getGingivaFromType(gingivaType: number): Gingiva {
  if (gingivaType === 1) return GINGIVA.MANDIBLE;
  else if (gingivaType === 2) return GINGIVA.MAXILLA;
  else throw Error('Invalid gingiva type');
}

export function parseToothFile(data: ArrayBufferLike): {
  teeth: MeshMap;
  missingTeeth: number[];
} {
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let teeth: DeltafaceConfig['teeth'] = new Map([]);
  let missingTeeth: DeltafaceConfig['missingTeeth'] = [];
  const toothCount = reader.readUint32();
  for (let i = 0; i < toothCount; i++) {
    const extracted = reader.readUint32();
    if (extracted) continue;
    const toothNumber = reader.readUint32();
    const vertexCount = reader.readUint32();
    const vertexData = parseVertices(reader, vertexCount);
    reader.offset = vertexData.dataOffset;
    const faceCount = reader.readUint32();
    const indexData = parseIndices(reader, faceCount);
    reader.offset = indexData.dataOffset;
    teeth.set(
      toothNumber,
      buildToothMesh(
        indexData.indices,
        vertexData.vertices,
        `tooth_${toothNumber}`
      )
    );
  }
  for (let i = 1; i <= 32; i++) {
    if (!teeth.has(i)) missingTeeth.push(i);
  }
  return { teeth, missingTeeth };
}

export function loadStartGingiva(
  data: ArrayBufferLike
): Pick<DeltafaceConfig, 'posStartGingivas' | 'gingivas'> {
  // Load N gingivas, number and type of gingiva are in the file (1 ~ mandible | 2 ~ maxilla)
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let posStartGingivas: DeltafaceConfig['posStartGingivas'] = new Map([]);
  let gingivas: DeltafaceConfig['gingivas'] = new Map([]);
  const gingivaCount = reader.readUint32();
  for (let i = 1; i <= gingivaCount; i++) {
    const gingivaType = reader.readUint32();
    const vertexCount = reader.readUint32();
    const vertexData = parseVertices(reader, vertexCount);
    reader.offset = vertexData.dataOffset;
    const faceCount = reader.readUint32();
    const indexData = parseIndices(reader, faceCount);
    reader.offset = indexData.dataOffset;
    const label = gingivaType === 1 ? 'mandible' : 'maxilla';
    posStartGingivas.set(getGingivaFromType(gingivaType), vertexData.vertices);
    gingivas.set(
      getGingivaFromType(gingivaType),
      buildGingivaMesh(
        indexData.indices,
        vertexData.vertices,
        `gingiva_${gingivaType}_${label}`
      )
    );
  }
  return { posStartGingivas, gingivas };
}

export function loadOptionalMeshes(
  data: ArrayBufferLike
): Pick<
  DeltafaceConfig,
  | 'optionalMeshes'
  | 'optionalMeshesTypes'
  | 'numbers'
  | 'wiresMandible'
  | 'wiresMaxilla'
> {
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let optionalMeshes: DeltafaceConfig['optionalMeshes'] = new Map([]);
  let optionalMeshesTypes: DeltafaceConfig['optionalMeshesTypes'] = new Map([]);
  let numbers: DeltafaceConfig['numbers'] = new Map([]);
  let wiresMandible: DeltafaceConfig['wiresMandible'] = new Map([]);
  let wiresMaxilla: DeltafaceConfig['wiresMaxilla'] = new Map([]);

  const optionalCount = reader.readInt32();

  // Load HDLs
  for (let k = 0; k < optionalCount; k++) {
    const hdlOptional = reader.readUint32();
    const typeNumber = parseInt(hdlOptional.toString().slice(0, 1));
    const vertexCount = reader.readUint32();
    const vertexData = parseVertices(reader, vertexCount);
    reader.offset = vertexData.dataOffset;
    const faceCount = reader.readUint32();
    const indexData = parseIndices(reader, faceCount);
    reader.offset = indexData.dataOffset;
    const geometry = buildBufferGeometry(
      indexData.indices,
      vertexData.vertices
    );
    const type = optionalMeshesTypes.get(hdlOptional);
    const mesh = new Mesh(geometry, buildHdlMaterial(type));
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `${typeNumber}_hdl_${hdlOptional}`;
    optionalMeshes.set(hdlOptional, mesh);
  }

  // Load numbers
  const totalSteps = reader.readInt32();
  for (let k = 0; k < totalSteps; k++) {
    const step = reader.readUint32();
    const vertexCount = reader.readUint32();
    const vertexData = parseVertices(reader, vertexCount);
    reader.offset = vertexData.dataOffset;
    const faceCount = reader.readUint32();
    const indexData = parseIndices(reader, faceCount);
    reader.offset = indexData.dataOffset;
    const mesh = buildStepNumberMesh(
      indexData.indices,
      vertexData.vertices,
      `numbers_${k}`
    );
    numbers.set(k, mesh);
  }

  //Load wires
  const totalWires = reader.readInt32();
  for (let k = 0; k < totalWires; k++) {
    const step = reader.readInt32();
    const type = reader.readInt32();
    const vertexCount = reader.readUint32();
    const vertexData = parseVertices(reader, vertexCount);
    reader.offset = vertexData.dataOffset;
    const faceCount = reader.readUint32();
    const indexData = parseIndices(reader, faceCount);
    reader.offset = indexData.dataOffset;
    const mesh = buildWireMesh(
      indexData.indices,
      vertexData.vertices,
      `wires_${k}`
    );
    if (type === 6) wiresMandible.set(step, mesh);
    if (type === 7) wiresMaxilla.set(step, mesh);
  }
  return {
    optionalMeshes,
    optionalMeshesTypes,
    numbers,
    wiresMandible,
    wiresMaxilla,
  };
}

//Load teeth frame -- Each tooth has an array of frame [0, nbMaxSteps]
export function loadSequenceInfo(
  data: ArrayBufferLike
): Pick<
  DeltafaceConfig,
  | 'frames'
  | 'hasIpr'
  | 'iprValues'
  | 'optionalMeshesFrames'
  | 'optionalMeshesToothNumbers'
  | 'optionalMeshesTypes'
  | 'optionalMeshesVisible'
  | 'totalSteps'
  | 'visibleIprs'
> {
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let frames: DeltafaceConfig['frames'] = new Map([]);
  let optionalMeshesVisible: DeltafaceConfig['optionalMeshesVisible'] =
    new Map();
  let optionalMeshesFrames: DeltafaceConfig['optionalMeshesFrames'] = new Map();
  let optionalMeshesTypes: DeltafaceConfig['optionalMeshesTypes'] = new Map([]);
  let optionalMeshesToothNumbers: DeltafaceConfig['optionalMeshesToothNumbers'] =
    new Map();
  let visibleIprs: DeltafaceConfig['visibleIprs'] = new Map();
  let iprValues: DeltafaceConfig['iprValues'] = new Map();
  let hasIpr: DeltafaceConfig['hasIpr'] = undefined;

  const totalSteps = reader.readInt32();
  const toothCount = reader.readInt32();
  for (let i = 0; i < toothCount; i++) {
    let toothFrames: Matrix4[] = [];
    const toothNumber = reader.readInt32();
    for (let step = 0; step < totalSteps; step++) {
      let matrix: number[] = [];
      for (let index = 0; index < 16; index++) {
        //Load matrix4x4
        matrix.push(reader.readFloat64());
      }
      toothFrames.push(buildMatrix(matrix));
    }
    frames.set(toothNumber, toothFrames);
  }

  const optionalCount = reader.readInt32();

  for (let k = 0; k < optionalCount; k++) {
    const optionalHDL = reader.readInt32();
    const optionalType = reader.readInt32();
    const toothNumber = reader.readInt32();
    optionalMeshesToothNumbers.set(optionalHDL, toothNumber);
    optionalMeshesTypes.set(optionalHDL, optionalType);

    let visibles: number[] = [];
    let optionFrames: Matrix4[] = [];
    for (let numberStep = 0; numberStep < totalSteps; numberStep++) {
      visibles.push(reader.readInt32());
      let matrix: number[] = [];
      for (let index = 0; index < 16; index++) {
        //Load matrix4x4
        matrix.push(reader.readFloat64());
      }
      optionFrames.push(buildMatrix(matrix));
    }
    optionalMeshesVisible.set(optionalHDL, visibles);
    optionalMeshesFrames.set(optionalHDL, optionFrames);
  }

  const iprCount = reader.readInt32();
  for (let k = 0; k < iprCount; k++) {
    try {
      const toothNumber = reader.readInt32();
      let visibles: number[] = [];
      let values: number[] = [];
      if (toothNumber === 32) continue;
      for (let step = 0; step < totalSteps; step++) {
        const isVisible = reader.readInt32();
        visibles.push(isVisible);
        const value = reader.readFloat64();
        values.push(value);
        if (hasIpr === undefined && value !== 0) hasIpr = true;
      }
      visibleIprs.set(toothNumber, visibles);
      iprValues.set(toothNumber, values);
    } catch (err) {
      // TODO: Handle "offset is outside the bounds of the DataView" error (pre-existing issue in Deltaface loader)
      console.error(err);
    }
  }

  return {
    frames,
    hasIpr,
    iprValues,
    optionalMeshesFrames,
    optionalMeshesToothNumbers,
    optionalMeshesTypes,
    optionalMeshesVisible,
    totalSteps,
    visibleIprs,
  };
}

// Load all gingivas deformations -- For each step we have the changement between step 0 and N
export function loadGingivaSteps(
  data: ArrayBufferLike
): Pick<DeltafaceConfig, 'mandibleGingivas' | 'maxillaGingivas'> {
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let mandibleGingivas: DeltafaceConfig['mandibleGingivas'] = new Map([]);
  let maxillaGingivas: DeltafaceConfig['maxillaGingivas'] = new Map([]);

  const totalSteps = reader.readInt32();
  for (let gingivaType = 0; gingivaType < 2; gingivaType++) {
    const type = reader.readInt32();
    for (let step = 1; step < totalSteps; step++) {
      const diffCount = reader.readInt32();
      let differences: Vector3Map = new Map([]);
      for (let currentDiff = 0; currentDiff < diffCount; currentDiff++) {
        const index = reader.readInt32();
        const x = reader.readInt32() / 100000;
        const y = reader.readInt32() / 100000;
        const z = reader.readInt32() / 100000;
        differences.set(index, new Vector3(x, y, z));
      }
      if (type === 1) mandibleGingivas.set(step, differences);
      else maxillaGingivas.set(step, differences);
    }
  }
  return { mandibleGingivas, maxillaGingivas };
}

export function loadIprsData(
  data: ArrayBufferLike
): Pick<DeltafaceConfig, 'iprPointsScene'> {
  const reader = new BufferReader(data, 4); // Ignore first 4 bytes (file version info)
  let iprPointsScene: DeltafaceConfig['iprPointsScene'] = new Map([]);
  const iprCount = reader.readInt32();
  for (let i = 0; i < iprCount; i++) {
    let pointsList: number[] = [];
    const toothNumber = reader.readInt32();
    const pointCount = reader.readInt32();
    for (let k = 0; k < pointCount; k++) {
      const x = reader.readFloat64();
      const y = reader.readFloat64();
      const z = reader.readFloat64();
      pointsList.push(x, y, z);
    }
    const position = new Float32BufferAttribute(pointsList, 3);
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', position);
    const point = new Points(geometry, buildPointsMaterial());
    point.visible = false;
    iprPointsScene.set(toothNumber, point);
  }
  return { iprPointsScene };
}

export async function parseDeltafaceZipFile(
  file: File | Blob,
  onProgress?: (progress: number) => void
): Promise<DeltafaceBuffers> {
  const parsedZip = await parseZipFile(file);
  let count = 0;
  const total = Object.keys(parsedZip).length;
  const incrementProgress = () => {
    count++;
    onProgress?.(count / total);
  };

  return await Object.values(DELTAFACE_FILES).reduce(
    async (accPromise, key) => {
      const acc = await accPromise;
      if (parsedZip[DELTAFACE_FILE_NAMES[key]]) {
        acc[key] = await parsedZip[DELTAFACE_FILE_NAMES[key]].arrayBuffer();
      }
      incrementProgress();
      return acc;
    },
    Promise.resolve({} as DeltafaceBuffers)
  );
}

export async function isDeltafaceZip(file: File | Blob): Promise<boolean> {
  if (file instanceof File && !isZip(file.name, file)) return false;
  try {
    const zip = await JSZip.loadAsync(file);
    const names = filterOutKeyValue(Object.values(zip.files), 'dir', true).map(
      (f) => f.name.split('/').pop()
    );
    return (
      names.includes(DELTAFACE_FILE_NAMES[DELTAFACE_FILES.TEETH]) &&
      names.includes(DELTAFACE_FILE_NAMES[DELTAFACE_FILES.SEQUENCE_INFOS])
    );
  } catch {
    return false;
  }
}
